import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { ImageFormat } from '../../services/imageService';
import { formatInfo } from '../../data/formatInfo';

interface FormatComparisonProps {
  from: ImageFormat;
  to: ImageFormat;
  slug: string;
}

const FormatComparison: React.FC<FormatComparisonProps> = ({ from, to, slug }) => {
  const { t } = useTranslation('convert');

  const fromInfo = formatInfo[from];
  const toInfo = formatInfo[to];

  return (
    <div className="max-w-6xl mx-auto mt-16 mb-12 space-y-12">
      
      {/* Why convert? */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {t('comparison.whyHeading', { from: from.toUpperCase(), to: to.toUpperCase() })}
        </h2>
        <p className="text-gray-700 leading-relaxed">
          {t(`comparison.why.${slug}`)}
        </p>
      </section>

      {/* Comparison table */}
      <section>
        <h3 className="text-xl font-bold text-gray-900 mb-6">{t('comparison.tableHeading')}</h3>
        <div className="overflow-x-auto bg-gray-200 rounded-card">
          <table className="min-w-full">
            <thead className="bg-gray-300 border-b border-gray-500">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">
                  {t('comparison.property')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">
                  {from.toUpperCase()}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">
                  {to.toUpperCase()}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-400">
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.compression')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${from}.compression`)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${to}.compression`)}
                </td>
              </tr>
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.transparency')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {fromInfo.transparency ? (
                    <Check size={18} className="text-green-600" />
                  ) : (
                    <X size={18} className="text-red-500" />
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {toInfo.transparency ? (
                    <Check size={18} className="text-green-600" />
                  ) : (
                    <X size={18} className="text-red-500" />
                  )}
                </td>
              </tr>
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.animation')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {fromInfo.animation ? (
                    <Check size={18} className="text-green-600" />
                  ) : (
                    <X size={18} className="text-red-500" />
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {toInfo.animation ? (
                    <Check size={18} className="text-green-600" />
                  ) : (
                    <X size={18} className="text-red-500" />
                  )}
                </td>
              </tr>
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.browserSupport')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${from}.browserSupport`)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${to}.browserSupport`)}
                </td>
              </tr>
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.fileSize')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${from}.typicalSize`)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${to}.typicalSize`)}
                </td>
              </tr>
              <tr className="hover:bg-gray-200">
                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                  {t('comparison.labels.bestFor')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${from}.bestFor`)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {t(`comparison.formats.${to}.bestFor`)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* About format cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[from, to].map((format) => {
          const info = formatInfo[format];
          const prosArray = t(`comparison.formats.${format}.pros`, { returnObjects: true }) as string[];
          const consArray = t(`comparison.formats.${format}.cons`, { returnObjects: true }) as string[];

          return (
            <div key={format} className="bg-gray-200 px-8 py-10 rounded-card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t('comparison.aboutHeading', { format: format.toUpperCase() })}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {t(`comparison.formats.${format}.fullName`)}
                  </p>
                  <p className="text-sm text-gray-500 mb-0">
                    {info.extension}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    {t('comparison.labels.pros')}
                  </h4>
                  <ul className="space-y-1 pl-0">
                    {prosArray.map((pro, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2 mb-2">
                        <Check size={16} className="text-green-600 shrink-0 mt-0.5" />
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    {t('comparison.labels.cons')}
                  </h4>
                  <ul className="space-y-1 pl-0">
                    {consArray.map((con, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2 mb-2">
                        <X size={16} className="text-red-500 shrink-0 mt-0.5" />
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </section>

    </div>
  );
};

export default FormatComparison;
